import React, {useEffect, useState, useRef} from "react";
import globalState from "../../../shared-services/globalState";
import BasicFarmyModal from "./BasicFarmyModal";
import useFullLoader from "../../loaders/useFullLoader";
import {ModalTitle, ModalBody} from "react-bootstrap";
import {useSelector, useSessionActions} from "../../../shared-services/redux-tiny";
import useModalQueue from "./useModalQueue";
import {t} from "../../../shared-services/translate";
import styled from "styled-components";
import currentTheme from "../../../react-themes/theme";
import restApiService from "../../shared/apiEndpoints";

const theme = currentTheme();
const {zipcodeInfo, zipcodeSubmit} = restApiService.location;

const ZipcodeModal = () => {
  const modalName = "zipcode";
  const maxZipLength = 4;

  const {currentZipcode, currentUser} = useSelector(state => state.session);
  const {currentModal} = useSelector(state => state.topLayer);
  const {setCurrentZipcode, setMarketingZone} = useSessionActions();
  const {closeModal, pushToQueue} = useModalQueue(modalName);
  const {launchLoader} = useFullLoader();

  const [hide, setHide] = useState(false);
  const [enteredZipcode, setEnteredZipcode] = useState("");
  const [zipcodeValid, setZipcodeValid] = useState(false);
  const [zipcodeComplete, setZipcodeComplete] = useState(false);

  const inputRef = useRef();

  const tKey = "zipcode_modal.2024";
  const tShort = (key) => t(`${tKey}.${key}`);

  const onClose = () => {
    const onCloseDelay = 800;
    setTimeout(() => {
      const {getGlobalState} = globalState();
      const globalZipcode = getGlobalState("session")?.currentZipcode;

      setEnteredZipcode(globalZipcode || "");
    }, onCloseDelay);
  };

  const closeZipcodeModal = () => {
    closeModal();
    onClose();
  };

  const onInputChanged = (event) => {
    const zipcode = event.target.value || "";

    // Allow only digits and trim any character above the max length.
    setEnteredZipcode(zipcode.replace(/\D/g, "").substring(0, maxZipLength));
  };

  const onZipcodeEntered = () => {
    zipcodeInfo(enteredZipcode).then(response => {
      setZipcodeComplete(true);
      const zipcodeData = response.data;

      if (zipcodeData?.hub_id) {
        setZipcodeValid(true);
      } else {
        setZipcodeValid(false);
      }
    });
  };

  const onZipcodeSubmit = (event = null) => {
    if (event) event?.preventDefault();

    if (zipcodeValid) {
      setHide(true);
      launchLoader({duration: 1000});

      const params = {
        zip: enteredZipcode
      };

      zipcodeSubmit(params).then(response => {
        const zipcode = response?.data?.zipcode?.name;
        const marketingZone = response?.data?.marketing_zone;

        if (zipcode) setCurrentZipcode(zipcode);
        if (marketingZone) setMarketingZone(marketingZone);
      }).finally(() => {
        closeZipcodeModal();
        setHide(false);
      });
    }
  };

  const onLoginClick = () => {
    pushToQueue(
      "LoginModal",
      {
        showBackLink: true,
        backLinkAction: {
          modal: {
            name: "zipcode"
          }
        }
      }
    );
    closeZipcodeModal();
  };

  useEffect(() => {
    if (zipcodeValid) onZipcodeSubmit();
  }, [zipcodeValid]);

  useEffect(() => {
    if (enteredZipcode?.length === maxZipLength && enteredZipcode !== currentZipcode?.toString()) {
      onZipcodeEntered();
    } else {
      setZipcodeComplete(false);
      setZipcodeValid(false);
    }
  }, [enteredZipcode]);

  useEffect(() => {
    if (currentZipcode) setEnteredZipcode(currentZipcode || "");
  }, [currentZipcode]);

  useEffect(() => {
    if (inputRef.current) inputRef.current?.focus();
  }, [inputRef.current]);

  useEffect(() => {
    const timeout = 500;

    if (currentModal?.name === modalName) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, timeout);
    }
  }, [currentModal]);

  const parentProps = {
    className: "zipcodeModal",
    modalName,
    onClose,
    hide
  };

  return <BasicFarmyModal {...parentProps}
                          className='farmy-zipcode-modal text-align-center'>
    <ModalTitle className='text-align-center margin-bottom-10'>
      {tShort("headline")}
    </ModalTitle>

    <ModalBody>
      <p className='text-align-center margin-bottom-20 font-family-alata'>
        {tShort("body")}
      </p>
      <div
        className='form-wrapper full-width flex flex-direction-column align-items-center justify-content-center margin-bottom-20'>
        <form onSubmit={onZipcodeSubmit}>
          <div
            className='input-wrapper flex align-items-center justify-content-space-between input-default overflow-hidden'>
            <StyledZipcodeInput
              ref={inputRef}
              maxLength={4}
              max={9999}
              theme={theme}
              type='number'
              placeholder={tShort("input_placeholder")}
              onChange={onInputChanged}
              value={enteredZipcode}
              autoFocus={true}
              zipcodeValid={zipcodeValid}
              zipcodeComplete={zipcodeComplete}
            />
            {/*TODO: Keep this commented block until it's confirmed that we won't use it!*/}
            {/*{zipcodeValid && zipcodeComplete && <SubmitButton onClick={onZipcodeSubmit}>*/}
            {/*  <FaArrowAltCircleRight/>*/}
            {/*</SubmitButton>}*/}
          </div>
        </form>
        {zipcodeComplete && !zipcodeValid
          ? <div className='text-color-danger font-family-alata text-align-center margin-top-10 font-weight-400'>
            {tShort("zipcode_error")}
          </div>
          : ""}
      </div>
      <div className='login-group text-align-center'>
        {currentUser?.id
          ? <></>
          : <>
            <p className='font-family-alata font-size-big'>
              {tShort("or")}
            </p>
            <div className='link-styled text-color-blue text-decoration-underline font-family-alata font-size-big'
                 onClick={onLoginClick}>
              {tShort("login")}
            </div>
          </>}
      </div>
    </ModalBody>
  </BasicFarmyModal>;
};

export default ZipcodeModal;

const StyledZipcodeInput = styled.input.attrs(props => ({
  className: `input-invisible text-align-center 
  ${props.zipcodeComplete && !props.zipcodeValid ? "text-color-danger" : ""}`
}))`
  max-width: 100%;
  font-size: 17px;
  font-weight: 700;
  flex-basis: 100%;
  padding: 0 10px;

  &::placeholder {
    font-weight: 100;
    color: ${theme.colors.farmy.lightgrey};
  }
`;

const LoginButton = styled.button.attrs({
  className: "btn btn-farmy btn-lg btn-green login-btn"
})`
  min-width: 138px;
`;

const SubmitButton = styled.div.attrs({
  className: "text-color-dimmed flex align-content-center justify-content-center cursor-pointer"
})`
  font-size: 24px;
  flex-shrink: 1;
  flex-basis: 0;
  margin-right: 5px;
  margin-left: -30px;
`;
